import React, { useMemo, useRef, useLayoutEffect, useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setConcursoAtual } from "src/reducers/concursos/concursosActions";
import { AnalyticsSetPageView, AnalyticsSetGame } from "src/reducers/analytics/analyticsActions";

import "src/static/tutorial/loteria-popular/default";

import "src/styles/menu.scss";
import { getSuperTimeIcon } from "src/utils/getSuperTimeIcon";
import Icon from "../../rifa/components/layout/icon";
import { hasPixParam, hasPreTicketConfig, hasPreTicketParam, isBLP, disableSuperTime } from "../../utils/getPreTickets";
import {
  handleScroll,
  handleScrollRepresentation,
} from "../../utils/handleScrollRepresentation";
import { loadState } from "../../modules/localStorage";

const Menu = ({ setSelectedContest, hidden = false, defaultSelection = "" }) => {
  const {
    objUser: { arrTipoJogo },
  } = useSelector((state) => state.usuario);
  const concursoAtual = useSelector((state) => state.concursos.concursoAtual);

  const params = new URLSearchParams(window.location.search);
  const showQuotation = params.get("quotation") === 'true';

  const dispatch = useDispatch();

  const menuRef = useRef(null);
  const menuBeforeRef = useRef(null);
  const menuAfterRef = useRef(null);
  const [showScrollButtons, setShowScrollButtons] = useState(true);
  const [showScrollButtonsLeft, setShowScrollButtonsLeft] = useState(false);
  const [showScrollButtonsRight, setShowScrollButtonsRight] = useState(true);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    function handleResize() {
      if (window) {
        setWidth(window.innerWidth);
        handleScrollRepresentation(
          menuRef.current,
          menuBeforeRef.current,
          menuAfterRef.current,
          () => {
            checkMenuScrollPosition();
          }
        );
      }
    }

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const checkMenuScrollPosition = () => {
    if (menuRef.current) {
      setShowScrollButtonsLeft(!(menuRef.current.scrollLeft < 50));
      setShowScrollButtonsRight(!(menuRef.current.scrollLeft >= ((menuRef.current.scrollWidth / 2) + 100)));
    }
  }

  useLayoutEffect(() => {
    if (menuRef.current) {
      if (menuRef.current.clientWidth < menuRef.current.scrollWidth) {
        setShowScrollButtons(true);

        handleScrollRepresentation(
          menuRef.current,
          menuBeforeRef.current,
          menuAfterRef.current,
          () => {
            checkMenuScrollPosition();
          }
        );
      } else {
        setShowScrollButtons(false);
        menuRef.current.removeEventListener("scroll", (e) =>
          handleScroll(
            e,
            menuBeforeRef.current,
            menuAfterRef.current,
            () => {
              checkMenuScrollPosition();
            })
        );
      }
    }

    return () => {
      menuRef.current.removeEventListener("scroll", (e) =>
        handleScroll(e,
          menuBeforeRef.current,
          menuAfterRef.current,
          () => {
            checkMenuScrollPosition();
          })
      );
    };
  }, [menuRef.current, arrTipoJogo, width]);

  const handleMoveScroll = (position) => {
    if (menuRef.current) {
      if (position === "left") {
        menuRef.current.scrollTo({
          left: menuRef.current.scrollLeft - 130,
          behavior: "smooth",
        });
      }

      if (position === "right") {
        menuRef.current.scrollTo({
          left: menuRef.current.scrollLeft + 130,
          behavior: "smooth",
        });
      }
    }
    checkMenuScrollPosition();
  };

  const handleSelectContest = (contest) => {
    if (contest && contest.objTipoJogo) {
      dispatch(AnalyticsSetPageView(contest.objTipoJogo.vchNome));
      dispatch(AnalyticsSetGame(contest.objTipoJogo.vchNome));
    }

    setSelectedContest(contest);
    dispatch(setConcursoAtual(contest));
  };

  const list = useMemo(() => {
    const arrSupertime =
      arrTipoJogo &&
      arrTipoJogo
        .filter(
          (tipoJogo) =>
            tipoJogo.objTipoJogo.bitConcurso &&
            tipoJogo.lstItemApuracao &&
            tipoJogo.lstItemApuracao.length > 0
        )
        .map((concurso) => {
          const img = getSuperTimeIcon(concurso.objTipoJogo.vchNome);

          return { ...concurso, type: "super-time", vchURLImagem: img };
        });

    const concursos =
      arrTipoJogo &&
      arrTipoJogo
        .filter(
          (tipoJogo) =>
            tipoJogo.objTipoJogo.bitConcurso &&
            (!tipoJogo.lstItemApuracao || tipoJogo.lstItemApuracao.length <= 0)
        )
        .map((concurso) => ({
          ...concurso,
          supertimeList:
            concurso.lstItem && concurso.lstItem.length > 0
              ? arrSupertime
              : undefined,
          type:
            concurso.lstItem && concurso.lstItem.length > 0
              ? "super-time"
              : "concurso",
        }))
        .sort(function (a, b) {
          if (a.objTipoJogo.vchNome < b.objTipoJogo.vchNome) {
            return -1;
          }
          if (a.objTipoJogo.vchNome > b.objTipoJogo.vchNome) {
            return 1;
          }
          return 0;
        })
        .sort((a, b) => (a.type === "super-time" ? -1 : 1));

    if (!concursos) return concursos;

    if (
      window["wweb-session"] &&
      window["wweb-session"].arrTipoJogo &&
      window["wweb-session"].arrTipoJogo.some(j => !!j.objTipoJogo.bitRifa)
    ) {
      const concursoRifa = {
        intCodigoRetorno: 0,
        type: "rifa",
        objTipoJogo: { sntTipoJogo: 999999, vchSigla: "R1", vchNome: "Rifa" },
      };
      concursos.unshift(concursoRifa);
    }

    const loterias =
      arrTipoJogo &&
      arrTipoJogo.filter((tipoJogo) => !tipoJogo.objTipoJogo.bitConcurso && !tipoJogo.objTipoJogo.bitRifa && !tipoJogo.objTipoJogo.bitBolao);

    if (loterias && loterias.length > 0) {
      const concursoLoteria = {
        type: "loteria-popular",
        intCodigoRetorno: 0,
        objTipoJogo: {
          sntTipoJogo: 3,
          vchSigla: "W",
          vchNome: "Loteria Popular",
          listModalidade: loterias,
        },
      };

      concursos.unshift(concursoLoteria);
    }

    const verifiedConcursosPath =
      concursos &&
      concursos.map((concurso, index) => {
        const submenu = ["Apostar"];

        if (
          ["loteria-popular", "concurso", "rifa", "super-time"].includes(
            concurso.type
          )
        ) {
          submenu.push("Como jogar");
        }

        if (
          ["loteria-popular", "concurso", "super-time"].includes(concurso.type) && showQuotation
        ) {
          submenu.push("Cotações");
        }

        if (
          ["loteria-popular", "concurso", "rifa", "super-time"].includes(
            concurso.type
          )
        ) {
          submenu.push("Resultados");
        }

        return {
          ...concurso,
          submenu,
        };
      });

    return verifiedConcursosPath;

  }, [arrTipoJogo]);

  const visibleList = useMemo(() => {
    return list.filter(item => {
      if(!isBLP()) return true;

      const isRifa = item.type === "rifa";
      const isPopular = item.type === "loteria-popular";
      const isSuperTime = item.type === "super-time";

      return item.type && !isRifa && !isPopular && (!isSuperTime || !disableSuperTime())
    });
  }, [list]);

  useLayoutEffect(() => {
    if (!concursoAtual && list && !defaultSelection) {
      const savedContest = loadState("contest");
      if (savedContest && !isBLP()) {
        handleSelectContest(savedContest);
      } else {
        const loteriaGame = visibleList.find((game) => game.type === "loteria-popular");
        const openDefaultGameAtHome = loteriaGame || visibleList[0];
        handleSelectContest(openDefaultGameAtHome);
      }
    }
  }, [list, visibleList]);

  useEffect(() => {
    if (defaultSelection) {
      const found = list.find(game => game.objTipoJogo.vchNome.toLowerCase().trim() === defaultSelection.toLowerCase().trim());
      console.log("found", defaultSelection, found, list.map(g => g.objTipoJogo.vchNome.toLowerCase().trim()));

      if (found) {
        handleSelectContest(found);
        return;
      }
    }
  }, [list, defaultSelection])

  const btMinhasApostas = {
    type: "none",
    intCodigoRetorno: 0,
    isMinhasApostas: true,
    isPreTickets: false,
    objTipoJogo: {
      sntTipoJogo: -1,
      vchSigla: "A",
      vchNome: "Minhas Apostas",
      listModalidade: [],
    }
  }

  const [shouldShowTicketsButton, setShouldShowTicketsButton] = useState(false);

  useEffect(() => {
    setShouldShowTicketsButton(!isBLP() && (hasPreTicketConfig() || hasPreTicketParam() || hasPixParam()));
  }, []);

  return (
    <div className="nav-wrapper" style={hidden ? { display: "none" } : undefined}>
      <div className={["menu-wrapper", shouldShowTicketsButton ? "" : "full-width"].join(" ")}>
        {(showScrollButtons && showScrollButtonsLeft) && (
          <button
            className="menu-arrow-left"
            onClick={() => handleMoveScroll("left")}
          >
            <Icon type="arrowLeft" color="red" key="a" style={{ stroke: "var(--menu-buttons)" }} />
          </button>
        )}
        <div className="menu" ref={menuRef}>
          <div className="menu-before" ref={menuBeforeRef}></div>
          <div className="menu-after" ref={menuAfterRef}></div>
          <div className="menu-container">
            {visibleList &&
              visibleList.map((concurso) => (
                <button
                  className={`menu-buttons ${concursoAtual &&
                    concursoAtual.objTipoJogo.sntTipoJogo ===
                    concurso.objTipoJogo.sntTipoJogo
                    ? "destaque"
                    : ""
                    }`}
                  key={concurso.objTipoJogo.vchNome}
                  onClick={() => handleSelectContest(concurso)}
                >
                  <p className="menu-button-name">
                    {concurso.objTipoJogo.vchNome.toLowerCase()}
                  </p>
                </button>
              ))}
          </div>
        </div>

        {(showScrollButtons && showScrollButtonsRight) && (
          <button
            className="menu-arrow-right"
            onClick={() => handleMoveScroll("right")}
          >
            <Icon type="arrowLeft" color="white" key="a" style={{ transform: 'rotate(180deg)', stroke: "var(--menu-buttons)" }} />
          </button>
        )}
      </div>
      {shouldShowTicketsButton &&
        <button
          className={`menu-button-ticket ${concursoAtual &&
            concursoAtual.objTipoJogo.sntTipoJogo === btMinhasApostas.objTipoJogo.sntTipoJogo
            ? "destaque"
            : ""
            }`}
          onClick={() => handleSelectContest(btMinhasApostas)}
        >
          <Icon type="wallet" color="white" style={{ stroke: "var(--menu-buttons)" }} />
          <p className="menu-button-name">
            Tickets
          </p>
        </button>
      }
    </div>
  );
};

export default Menu;



// WEBPACK FOOTER //
// ./src/newDesign/components/menu.jsx